import {functions, httpsCallable} from "@/firebase/firebaseConfig";
import {readPersistedAffiliateRefCode} from "@/lib/AffiliateLink";
import {readPublicVideoClaimContext} from "@/lib/PublicVideoClaim";
import {getSessionStorageItem, setLocalStorageItem, setSessionStorageItem} from "@/lib/storageUtils";

type ToltGlobal = {
    signup?: (email: string) => void | Promise<void>;
};

type ToltData = {
    customer_id?: string | null;
};

type ToltLinkCustomerToPartnerInput = {
    claimToken?: string;
    refCode?: string;
    source?: string;
    status?: "lead" | "trialing" | "active" | "canceled";
};

type ToltLinkCustomerToPartnerResponse = {
    success: boolean;
    partnerId: string;
    toltCustomerId: string;
    idempotent: boolean;
    source: "claim_owner_affiliate" | "claim_ref" | "request_ref" | "existing";
};

declare global {
    interface Window {
        tolt?: ToltGlobal;
        tolt_data?: ToltData | null;
        tolt_referral?: string | { id?: string; ref?: string; referral?: string; code?: string } | null;
    }
}

const REF_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

const cleanRefCode = (value?: string | null): string | null => {
    const trimmed = (value || "").trim();
    if (!trimmed) return null;
    const withoutBraces = trimmed.replace(/^\{+|\}+$/g, "").trim();
    return REF_CODE_PATTERN.test(withoutBraces) ? withoutBraces : null;
};

const readRefCodeFromQuery = (): string | null => {
    if (typeof window === "undefined") return null;
    return cleanRefCode(new URLSearchParams(window.location.search).get("ref"));
};

const readRefCodeFromToltGlobal = (): string | null => {
    if (typeof window === "undefined") return null;
    const referral = window.tolt_referral;
    if (typeof referral === "string") return cleanRefCode(referral);
    return (
        cleanRefCode(referral?.id) ||
        cleanRefCode(referral?.ref) ||
        cleanRefCode(referral?.referral) ||
        cleanRefCode(referral?.code)
    );
};

const hydrateToltReferralContext = (): string | null => {
    if (typeof window === "undefined") return null;

    const claimContext = readPublicVideoClaimContext();
    const resolvedRefCode =
        readRefCodeFromQuery() ||
        cleanRefCode(claimContext?.resolvedRefCode) ||
        readPersistedAffiliateRefCode() ||
        readRefCodeFromToltGlobal();

    if (!resolvedRefCode) {
        return null;
    }

    // Ensure fallback referrals can be consumed by tolt.js even when query params were dropped.
    setLocalStorageItem("affiliateRefCode", resolvedRefCode);
    window.tolt_referral = resolvedRefCode;

    const query = new URLSearchParams(window.location.search);
    if (!query.get("ref")) {
        query.set("ref", resolvedRefCode);
        const nextUrl = `${window.location.pathname}?${query.toString()}${window.location.hash || ""}`;
        window.history.replaceState(window.history.state, "", nextUrl);
    }

    return resolvedRefCode;
};

const readClaimContextForAttribution = (): {claimToken?: string; source?: string} => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const claimContext = readPublicVideoClaimContext();
    const claimToken = params.get("claim") || claimContext?.claimToken || undefined;
    const source = params.get("src") || claimContext?.sourceContext || undefined;
    return {claimToken, source};
};

const linkCustomerToPartnerBackend = async (input: ToltLinkCustomerToPartnerInput) => {
    const fn = httpsCallable<ToltLinkCustomerToPartnerInput, ToltLinkCustomerToPartnerResponse>(
        functions,
        "tolt-linkCustomerToPartner"
    );
    const result = await fn(input);
    return result.data;
};

export function trackToltReferralSignup(email?: string | null): void {
    if (typeof window === "undefined") return;
    const safeEmail = (email || "").trim();
    if (!safeEmail) return;
    const resolvedRefCode = hydrateToltReferralContext();
    const dedupeKey = `tolt-signup:${safeEmail.toLowerCase()}`;
    if (getSessionStorageItem(dedupeKey) === "done") return;

    const maxAttempts = 15;
    let attempts = 0;

    const trySignup = async () => {
        attempts += 1;
        const {claimToken, source} = readClaimContextForAttribution();
        const signupFn = window.tolt?.signup;
        const toltData = window.tolt_data;
        const hasScriptLoaded = typeof toltData !== "undefined";

        // Backend-owned attribution (deterministic and idempotent) is primary.
        if (claimToken || resolvedRefCode) {
            try {
                const response = await linkCustomerToPartnerBackend({
                    claimToken,
                    refCode: resolvedRefCode || undefined,
                    source,
                    status: "lead",
                });
                if (response?.success) {
                    setSessionStorageItem(dedupeKey, "done");
                    return;
                }
            } catch (error) {
                // Fall back to tolt.js so we don't regress while backend rolls out.
                console.warn("[Tolt] backend attribution link failed; falling back to tolt.js", error);
            }
        }

        if (typeof signupFn !== "function" || !hasScriptLoaded) {
            if (attempts < maxAttempts) {
                window.setTimeout(trySignup, 400);
            }
            return;
        }

        // When ref params were dropped during auth redirects, fallback hydration can still
        // recover a referral code. In that case we still attempt signup attribution.
        if (!toltData && !resolvedRefCode) {
            return;
        }

        // Customer is already associated with this click in Tolt.
        if (toltData?.customer_id) {
            setSessionStorageItem(dedupeKey, "done");
            return;
        }

        Promise.resolve(signupFn(safeEmail))
            .then(() => {
                setSessionStorageItem(dedupeKey, "done");
            })
            .catch((error: any) => {
                // 409 means the customer already exists in Tolt; treat as success.
                const message = (error?.message || "").toString().toLowerCase();
                const status = Number(error?.status || error?.response?.status || 0);
                if (status === 409 || message.includes("already exists")) {
                    setSessionStorageItem(dedupeKey, "done");
                    return;
                }
                console.warn("[Tolt] signup tracking failed", error);
            });
    };

    void trySignup();
}

